import { gatewayApi as api } from './gatewayApi';
import { WorkOrder } from './workOrderApi';

// NCR Interfaces
export interface NCR {
  id: string;
  ncr_folio_num: string;
  work_order_id: string;
  operation_id: string;
  part_id: string;
  route_card_id: string;
  created_date: string;
  created_by: string;
  applicable_concession?: string;
  closure_status: 'Open' | 'Closed';
  closure_sign?: string;
  
  // Root Cause Analysis (5W's + 1H)
  what_txt?: string;
  where_txt?: string;
  where_machine_id?: string;
  where_dept_id?: string;
  who_txt?: string;
  when_txt?: string;
  stationtxt?: string; // Shift
  root_cause_txt?: string;
  action_taken?: string;
  comments?: string;
  responsible_sign?: string;
  supervisor_sign?: string;
  
  // Approval/Closing
  user_approved_id?: string;
  approved_date?: string;
  ca_document?: Document;
  
  // Related entities
  work_order?: WorkOrder;
  operation?: Operation;
  part?: Part;
  route_card?: RouteCard;
  serial_data?: NCRSerial[];
  serials?: NCRSerial[];
}

export interface NCRSerial {
  id: string;
  ncr_detail_id: string;
  serial_num: string;
  serial_status_id: number;
  qn_num?: string;
  comments?: string;
  status?: SerialStatus;
  part_defects?: PartDefect[];
}

export interface PartDefect {
  id: string;
  ncr_detail_id: string;
  nc_type_id: string;
  nc_sub_type_id: string;
  part_attribute_id: string;
  part_sec_attribute_id?: string;
  part_terc_attribute_id?: string;
  origin_dim_attribute?: string;
  actual_dim_attribute?: string;
  sight_gauge: boolean;
  cmm: boolean;
  nogo_over_tolerance: boolean;
  nogo_under_tolerance: boolean;
}

export interface NCType {
  id: string;
  name: string;
  description?: string;
  active: boolean;
}

export interface NCSubType {
  id: string;
  name: string;
  description?: string;
  nc_type_id: string;
  active: boolean;
}

export interface PartAttribute {
  id: string;
  name: string;
  description?: string;
  active: boolean;
}

export interface SerialStatus {
  id: number;
  name: string;
  description?: string;
  active: boolean;
}

export interface Operation {
  id: string;
  operation_num: string;
  name: string;
  description?: string;
  route_card_id: string;
}

export interface Part {
  id: string;
  number: string;
  description: string;
  active: boolean;
}

export interface RouteCard {
  id: string;
  name: string;
  description?: string;
  part_id: string;
  active: boolean;
}

export interface Machine {
  id: string;
  name: string;
  description?: string;
  active: boolean;
}

export interface Department {
  id: string;
  name: string;
  description?: string;
  active: boolean;
}

export interface User {
  id: string;
  user_name: string;
  first_name: string;
  last_name: string;
  email: string;
  active: boolean;
}

export interface Document {
  id: string;
  file_name: string;
  file_path: string;
  file_data?: string; // Base64 encoded
  upload_date: string;
  uploaded_by: string;
}

// Request/Response Types
export interface CreateNCRRequest {
  work_order_id: string;
  operation_id: string;
  serialData: {
    serial_num: string;
    serial_status_id: number;
  }[];
}

export interface SearchNCRsRequest {
  page_num?: number;
  page_size?: number;
  sort?: string;
  filter?: {
    ncr_folio_num?: string;
    closure_status?: 'Open' | 'Closed';
    work_order_id?: string;
    part_id?: string;
    created_date?: string;
  };
}

export interface SearchNCRsResponse {
  items: NCR[];
  total_count: number;
  page_num: number;
  page_size: number;
}

export interface NCRReportData {
  ncr: NCR;
  serial_details: NCRSerial[];
  part_defects: PartDefect[];
  company_logo?: string;
}

export interface AddDefectRequest {
  ncr_detail_id: string;
  nc_type_id: string;
  nc_sub_type_id?: string;
  part_attribute_id: string;
  part_sec_attribute_id?: string;
  part_terc_attribute_id?: string;
  origin_dim_attribute?: string;
  actual_dim_attribute?: string;
  sight_gauge: boolean;
  cmm: boolean;
  nogo_over_tolerance: boolean;
  nogo_under_tolerance: boolean;
}

export interface UpdateSerialDispositionRequest {
  serial_id: string;
  serial_status_id: number;
  qn_num?: string;
  comments?: string;
}

// API Service
export const ncrApi = {
  // NCR CRUD Operations
  getAll: (params?: SearchNCRsRequest): Promise<SearchNCRsResponse> =>
    api.post('/ncr/search', params || {}),

  getById: (id: string): Promise<NCR> =>
    api.get(`/ncr/${id}`),

  create: (data: CreateNCRRequest): Promise<NCR> =>
    api.post('/ncr/', data),

  update: (data: Partial<NCR>): Promise<NCR> =>
    api.put('/ncr/', data),

  delete: (id: string): Promise<void> =>
    api.delete(`/ncr/${id}`),

  // Serial Management
  getSerials: (ncrId: string): Promise<NCRSerial[]> =>
    api.get(`/ncr/${ncrId}/serials`),

  updateSerial: (data: UpdateSerialDispositionRequest): Promise<NCRSerial> =>
    api.put('/ncr/serial', data),

  deleteSerial: (serialId: string): Promise<void> =>
    api.delete(`/ncr/serial/${serialId}`),

  // Stored Procedure Calls
  getOperationFailureWorkOrders: (): Promise<{ data: any[] }> =>
    api.post('/ncr/proc/getoperationfailurewo', {}),

  // Defect Management
  addDefect: (ncrDetailId: string, defect: AddDefectRequest): Promise<PartDefect> =>
    api.post(`/partdefects/`, { ...defect, ncr_detail_id: ncrDetailId }),

  updateDefect: (defect: PartDefect): Promise<PartDefect> =>
    api.put('/partdefects/', defect),

  deleteDefect: (defectId: string): Promise<void> =>
    api.delete(`/partdefects/${defectId}`),

  // Dropdown Data
  getWorkOrders: (): Promise<WorkOrder[]> =>
    api.get('/workorder/'),

  getOperations: (workOrderId?: string): Promise<Operation[]> =>
    api.get('/operation/', workOrderId ? { filter: { work_order_id: workOrderId } } : undefined),

  getSerialsByWorkOrder: (workOrderId: string, operationId?: string): Promise<NCRSerial[]> =>
    api.get('/serial/', { 
      filter: { 
        work_order_id: workOrderId,
        ...(operationId && { operation_id: operationId })
      }
    }),

  getNCTypes: (): Promise<NCType[]> =>
    api.get('/nctype/'),

  getNCSubTypes: (ncTypeId?: string): Promise<NCSubType[]> =>
    api.get('/ncsubtype/', ncTypeId ? { filter: { nc_type_id: ncTypeId } } : undefined),

  getPartAttributes: (): Promise<PartAttribute[]> =>
    api.get('/partattributes/'),

  getPartSecAttributes: (): Promise<PartAttribute[]> =>
    api.get('/partsecattributes/'),

  getPartTercAttributes: (): Promise<PartAttribute[]> =>
    api.get('/parttercattributes/'),

  getSerialStatuses: (): Promise<SerialStatus[]> =>
    api.get('/serialstatus/'),

  getMachines: (): Promise<Machine[]> =>
    api.get('/machine/'),

  getDepartments: (): Promise<Department[]> =>
    api.get('/department/'),

  getUsers: (): Promise<User[]> =>
    api.get('/user/'),

  // Report Generation
  generateReport: (ncrId: string): Promise<NCRReportData> =>
    api.get(`/reports/ncr/${ncrId}`),

  printReport: async (ncrId: string): Promise<Blob> => {
    const config = await api.get('/appconfig/carbone_data') as {
      template_id_ncr: string;
      carbone_id: string;
    };

    const reportData = await ncrApi.generateReport(ncrId);
    
    const carboneData = {
      data: reportData,
      template: config.template_id_ncr
    };

    const response = await fetch('https://render.carbone.io/render', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'carbone-authorization': `Bearer ${config.carbone_id}`
      },
      body: JSON.stringify(carboneData)
    });

    if (!response.ok) {
      throw new Error('Failed to generate PDF report');
    }

    const result = await response.json() as { render_id: string };
    const pdfResponse = await fetch(`https://render.carbone.io/render/${result.render_id}`);
    
    if (!pdfResponse.ok) {
      throw new Error('Failed to download PDF report');
    }

    return pdfResponse.blob();
  },

  // Step-wise NCR Filling
  saveGeneralDetails: (ncrId: string, data: Partial<NCR>): Promise<NCR> =>
    api.put('/ncr/step1', { id: ncrId, ...data }),

  saveSerialsDisposition: (ncrId: string, serialData: NCRSerial[]): Promise<NCR> =>
    api.put('/ncr/step2', { id: ncrId, serial_data: serialData }),

  saveClosing: (ncrId: string, data: {
    user_approved_id: string;
    approved_date: string;
    ca_document?: Document;
    closure_sign: string;
  }): Promise<NCR> =>
    api.put('/ncr/step3', { id: ncrId, ...data }),

  // Utility Functions
  validateNCR: (ncr: Partial<NCR>): string[] => {
    const errors: string[] = [];

    if (!ncr.work_order_id) {
      errors.push('Work Order is required');
    }

    if (!ncr.operation_id) {
      errors.push('Operation is required');
    }

    if (!ncr.serial_data || ncr.serial_data.length === 0) {
      errors.push('At least one serial must be selected');
    }

    return errors;
  },

  validateDefect: (defect: Partial<PartDefect>): string[] => {
    const errors: string[] = [];

    if (!defect.nc_type_id) {
      errors.push('Non-Conformance Type is required');
    }

    if (!defect.part_attribute_id) {
      errors.push('Primary Location is required');
    }

    return errors;
  },

  formatNCRNumber: (ncrId: string): string => {
    // Format NCR number according to business rules
    const date = new Date();
    const year = date.getFullYear().toString().slice(-2);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    return `NCR-${year}${month}-${ncrId.padStart(4, '0')}`;
  },

  calculateNCRStatistics: (ncrs: NCR[]): {
    total_open: number;
    total_closed: number;
    total_this_month: number;
    average_close_time: number;
  } => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    const totalOpen = ncrs.filter(ncr => ncr.closure_status === 'Open').length;
    const totalClosed = ncrs.filter(ncr => ncr.closure_status === 'Closed').length;
    const totalThisMonth = ncrs.filter(ncr => 
      new Date(ncr.created_date) >= startOfMonth
    ).length;

    // Calculate average close time (placeholder implementation)
    const closedNCRs = ncrs.filter(ncr => ncr.closure_status === 'Closed' && ncr.approved_date);
    const averageCloseTime = closedNCRs.reduce((acc, ncr) => {
      const created = new Date(ncr.created_date);
      const closed = new Date(ncr.approved_date!);
      return acc + (closed.getTime() - created.getTime());
    }, 0) / (closedNCRs.length || 1);

    return {
      total_open: totalOpen,
      total_closed: totalClosed,
      total_this_month: totalThisMonth,
      average_close_time: Math.round(averageCloseTime / (1000 * 60 * 60 * 24)) // Convert to days
    };
  },

  exportToCSV: (ncrs: NCR[]): string => {
    const headers = [
      'NCR Folio',
      'Work Order',
      'Operation',
      'Part Number',
      'Status',
      'Created Date',
      'Created By',
      'Root Cause',
      'Action Taken',
      'Approved Date'
    ];

    const rows = ncrs.map(ncr => [
      ncr.ncr_folio_num,
      ncr.work_order?.wo_num || '',
      ncr.operation?.operation_num || '',
      ncr.part?.number || '',
      ncr.closure_status,
      new Date(ncr.created_date).toLocaleDateString(),
      ncr.created_by,
      ncr.root_cause_txt || '',
      ncr.action_taken || '',
      ncr.approved_date ? new Date(ncr.approved_date).toLocaleDateString() : ''
    ]);

    return [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');
  },

  downloadCSV: (csvContent: string, filename: string): void => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
}; 